import { useEffect, useRef, useState } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { NavBar } from './NavBar';

const SYSTEM_PROMPT = `You are the official AI assistant for holol Club (نادي حلول), a premium consulting platform that connects investors and professionals with top-tier service providers worldwide.

holol Club offers 6 core service categories:
1. Legal Advisory — corporate formation, contracts, real estate law, labor law, IP, litigation (25 services)
2. Accounting & Audit — bookkeeping, taxation, CFO services, financial modeling, forensic accounting (25 services)
3. Financing Solutions — bank credit, venture capital, Islamic finance, government grants, debt restructuring (25 services)
4. Feasibility Studies — market analysis, SWOT, PESTEL, financial modeling, ROI/IRR, business plans (25 services)
5. Advertising & Marketing — branding, digital marketing, SEO, content production, PR, offline marketing (25 services)
6. Residence & Immigration — citizenship by investment, golden visas, entrepreneur visas, relocation support (25 services)

Your role:
- Answer questions about holol Club services clearly and professionally
- Help users identify which service fits their needs
- Be concise, warm, and helpful
- Support both English and Arabic — respond in the same language the user writes in
- Do not invent specific pricing or timelines; invite users to register or contact the team for detailed quotes
- Always end with a helpful next step (e.g., "You can register on our platform or contact our team for a free consultation.")

Keep responses under 200 words unless the question requires more detail.`;

function buildGenAI() {
  const key = process.env.REACT_APP_GEMINI_API_KEY;
  if (!key || key === 'your_gemini_api_key_here') return null;
  return new GoogleGenerativeAI(key);
}

const genAI = buildGenAI();

export function AIChatPage() {
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  const welcomeMessage = isAr
    ? 'مرحباً! أنا مساعد نادي حلول الذكي. كيف يمكنني مساعدتك اليوم؟'
    : "Hello! I'm the holol Club AI assistant. How can I help you today?";

  useEffect(() => {
    setMessages([{ role: 'assistant', text: welcomeMessage }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    if (!genAI) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: isAr
            ? 'لم يتم تكوين مفتاح Gemini API بعد. يُرجى إضافته في ملف .env.'
            : 'Gemini API key is not configured yet. Please add it to the .env file.',
        },
      ]);
      setLoading(false);
      return;
    }

    try {
      const model = genAI.getGenerativeModel({ model: 'gemini-2.5-flash-lite' });
      const prior = messages.slice(1);
      const history = prior.map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        parts: [{ text: m.text }],
      }));
      const chat = model.startChat({
        history,
        systemInstruction: { role: 'system', parts: [{ text: SYSTEM_PROMPT }] },
      });
      const result = await chat.sendMessage(text);
      const reply = result.response.text();
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    } catch (err) {
      console.error('Gemini error:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: isAr
            ? 'حدث خطأ. يُرجى المحاولة مرة أخرى.'
            : 'Something went wrong. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="min-h-screen bg-[#000c1e] flex flex-col" dir={isAr ? 'rtl' : 'ltr'}>
      <NavBar t={t} language={language} toggleLanguage={toggleLanguage} />

      <div className="flex-1 flex flex-col pt-20 max-w-3xl mx-auto w-full px-4 pb-6">
        {/* Page header */}
        <div className="flex items-center gap-4 py-8">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary text-white rounded-full font-semibold text-sm hover:bg-secondary/85 transition-colors shadow-md"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            {isAr ? 'الرئيسية' : 'Home'}
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shadow-lg">
              <span className="material-symbols-outlined text-white text-xl">smart_toy</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">
                {isAr ? 'مساعد نادي حلول الذكي' : 'holol AI Assistant'}
              </h1>
              <p className="text-white/50 text-xs">
                {isAr ? 'مدعوم بالذكاء الاصطناعي' : 'Powered by AI'}
              </p>
            </div>
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col bg-white/5 rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" style={{ minHeight: 0, maxHeight: 'calc(100vh - 280px)' }}>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? (isAr ? 'justify-start' : 'justify-end') : (isAr ? 'justify-end' : 'justify-start')}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1 mx-2">
                    <span className="material-symbols-outlined text-white text-sm">smart_toy</span>
                  </div>
                )}
                <div
                  className={`max-w-[75%] px-5 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-secondary text-white rounded-br-sm'
                      : 'bg-white text-on-surface rounded-bl-sm shadow-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className={`flex ${isAr ? 'justify-end' : 'justify-start'}`}>
                <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-1 mx-2">
                  <span className="material-symbols-outlined text-white text-sm">smart_toy</span>
                </div>
                <div className="bg-white rounded-2xl rounded-bl-sm px-5 py-3.5 shadow-sm flex gap-1.5 items-center">
                  <span className="w-2 h-2 rounded-full bg-secondary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-secondary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-secondary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input bar */}
          <div className="flex items-end gap-3 px-5 py-4 border-t border-white/10 bg-white/5">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
              placeholder={isAr ? 'اكتب سؤالك هنا...' : 'Ask me anything...'}
              className="flex-1 resize-none rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-secondary/50 max-h-32 overflow-y-auto"
              style={{ lineHeight: '1.5' }}
            />
            <button
              type="button"
              onClick={send}
              disabled={!input.trim() || loading}
              className="flex-shrink-0 w-11 h-11 rounded-xl bg-secondary text-white flex items-center justify-center hover:bg-secondary/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label={isAr ? 'إرسال' : 'Send'}
            >
              <span className="material-symbols-outlined">send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
